import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Checkbox, IconButton, Stack, TextField, Tooltip, Typography} from 'views/components';
import {AddOutlined, DeleteOutline} from 'views/icons';
import {Translate, translate} from 'views/module';

// Mirrors the engine's Joi caps on PUT /domain_guide/{rn}/rubric so admins
// never hit a raw 400: passingDefinition ≤5000, ≤20 categories, label and key
// ≤200, description ≤1000. Auto-fail conditions (≤500 each) are category
// labels, so the label cap covers them.
const PASSING_DEFINITION_MAX_CHARS = 5000;
const MAX_FAILURE_CATEGORIES = 20;
const CATEGORY_LABEL_MAX_CHARS = 200;
const CATEGORY_KEY_MAX_CHARS = 200;
const CATEGORY_DESCRIPTION_MAX_CHARS = 1000;

// Stable snake_case keys generated from labels; the key never changes once a
// category exists, even if its label is edited later.
const toSnakeCaseKey = (label) => {
  return `${label || ''}`
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
};

// The backend's auto-fail paging matches on the missed_escalation and
// policy_violation key strings, so the starter template must use them exactly.
const buildStarterCategories = () => ([
  {
    key: 'missed_escalation',
    label: 'Missed escalation',
    description: translate('domainGuide.Detail.Rubric.StarterDescriptions.MissedEscalation'),
    autoFail: true,
  },
  {
    key: 'wrong_information',
    label: 'Wrong information',
    description: translate('domainGuide.Detail.Rubric.StarterDescriptions.WrongInformation'),
    autoFail: false,
  },
  {
    key: 'overpromised_too_agreeable',
    label: 'Overpromised / too agreeable',
    description: translate('domainGuide.Detail.Rubric.StarterDescriptions.Overpromised'),
    autoFail: false,
  },
  {
    key: 'policy_violation',
    label: 'Policy violation',
    description: translate('domainGuide.Detail.Rubric.StarterDescriptions.PolicyViolation'),
    autoFail: true,
  },
  {
    key: 'poor_tone',
    label: 'Poor tone',
    description: translate('domainGuide.Detail.Rubric.StarterDescriptions.PoorTone'),
    autoFail: false,
  },
  {
    key: 'no_follow_up',
    label: 'No follow-up',
    description: translate('domainGuide.Detail.Rubric.StarterDescriptions.NoFollowUp'),
    autoFail: false,
  },
]);

// The editor writes each auto-fail category's label into autoFailConditions;
// conditions that don't match a category label (e.g. written via the API) are
// preserved untouched and re-sent on save.
const buildEditorState = (rubric) => {
  if (!rubric) {
    return {
      passingDefinition: '',
      categories: buildStarterCategories(),
      extraAutoFailConditions: [],
    };
  }

  const autoFailConditions = Array.isArray(rubric.autoFailConditions) ? rubric.autoFailConditions : [];
  const categories = (rubric.failureCategories || []).map((category) => ({
    key: category.key,
    label: category.label,
    description: category.description || '',
    autoFail: autoFailConditions.includes(category.label),
  }));
  const categoryLabels = categories.map((category) => category.label);

  return {
    passingDefinition: rubric.passingDefinition || '',
    categories,
    extraAutoFailConditions: autoFailConditions.filter((condition) => !categoryLabels.includes(condition)),
  };
};

const fieldLabelSx = {
  fontWeight: 500,
  fontSize: '11px',
  lineHeight: '14px',
  color: '#A8A49E',
};

const DomainGuideRubric = (props) => {
  const {
    domainGuideData,
    updateRubric,
  } = props;

  const [editorState, setEditorState] = React.useState(() => buildEditorState(domainGuideData.rubric));
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveFailed, setSaveFailed] = React.useState(false);

  React.useEffect(() => {
    setEditorState(buildEditorState(domainGuideData.rubric));
  }, [domainGuideData.rubric]);

  const setPassingDefinition = (passingDefinition) => {
    setEditorState((currentState) => ({
      ...currentState,
      passingDefinition,
    }));
  };

  const setCategoryField = (index, field, value) => {
    setEditorState((currentState) => ({
      ...currentState,
      categories: currentState.categories.map((category, categoryIndex) => (
        categoryIndex === index ?
          {
            ...category,
            [field]: value,
          } :
          category
      )),
    }));
  };

  const addCategory = () => {
    setEditorState((currentState) => ({
      ...currentState,
      categories: [
        ...currentState.categories,
        {
          key: '',
          label: '',
          description: '',
          autoFail: false,
        },
      ],
    }));
  };

  const removeCategory = (index) => {
    setEditorState((currentState) => ({
      ...currentState,
      categories: currentState.categories.filter((category, categoryIndex) => categoryIndex !== index),
    }));
  };

  const canAddCategory = editorState.categories.length < MAX_FAILURE_CATEGORIES;
  const canSave = Boolean(editorState.passingDefinition.trim()) &&
    editorState.categories.length <= MAX_FAILURE_CATEGORIES &&
    editorState.categories.every((category) => category.label.trim());

  const saveRubric = async () => {
    if (!canSave || isSaving) {
      return;
    }

    const usedKeys = new Set(editorState.categories.map((category) => category.key).filter(Boolean));
    const failureCategories = editorState.categories.map((category) => {
      let key = category.key;

      if (!key) {
        // Leave headroom for the dedupe suffix under the engine's key cap.
        const baseKey = (toSnakeCaseKey(category.label) || 'category').slice(0, CATEGORY_KEY_MAX_CHARS - 10);
        key = baseKey;
        let suffix = 2;

        while (usedKeys.has(key)) {
          key = `${baseKey}_${suffix}`;
          suffix += 1;
        }
        usedKeys.add(key);
      }

      return {
        key,
        label: category.label.trim(),
        description: category.description,
      };
    });
    const autoFailConditions = [
      ...editorState.categories
          .filter((category) => category.autoFail)
          .map((category) => category.label.trim()),
      ...editorState.extraAutoFailConditions,
    ];

    setIsSaving(true);
    setSaveFailed(false);

    const saved = await updateRubric({
      passingDefinition: editorState.passingDefinition,
      failureCategories,
      autoFailConditions,
    });

    setSaveFailed(!saved);
    setIsSaving(false);
  };

  const rubricUpdatedAtDate = new Date(domainGuideData.rubricUpdatedAt);
  const rubricUpdatedAtLabel = Number.isNaN(rubricUpdatedAtDate.getTime()) ?
    '' :
    rubricUpdatedAtDate.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E8E4DE',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      <Stack sx={{gap: 2, px: 3, py: 2}}>
        <Stack sx={{gap: '4px'}}>
          <Typography sx={fieldLabelSx}>
            <Translate expression='domainGuide.Detail.Rubric.Title' />
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              lineHeight: '16px',
              color: '#6B6761',
            }}
          >
            <Translate expression='domainGuide.Detail.Rubric.Subtitle' />
          </Typography>
        </Stack>
        <Stack sx={{gap: '8px'}}>
          <Typography sx={fieldLabelSx}>
            <Translate expression='domainGuide.Detail.Rubric.PassingDefinitionLabel' />
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            inputProps={{maxLength: PASSING_DEFINITION_MAX_CHARS}}
            placeholder={translate('domainGuide.Detail.Rubric.PassingDefinitionPlaceholder')}
            value={editorState.passingDefinition}
            onChange={setPassingDefinition}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                fontSize: '14px',
                lineHeight: '150%',
              },
            }}
          />
        </Stack>
        <Stack sx={{gap: '8px'}}>
          <Typography sx={fieldLabelSx}>
            <Translate expression='domainGuide.Detail.Rubric.FailureCategoriesLabel' />
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              lineHeight: '16px',
              color: '#6B6761',
            }}
          >
            <Translate expression='domainGuide.Detail.Rubric.FailureCategoriesHint' />
          </Typography>
          <Stack sx={{gap: 1.5}}>
            {
              editorState.categories.map((category, index) => (
                <Box
                  key={category.key || `new-category-${index}`}
                  sx={{
                    border: '1px solid #E8E4DE',
                    borderRadius: '8px',
                    p: 1.5,
                  }}
                >
                  <Stack sx={{gap: 1}}>
                    <Stack direction='row' sx={{gap: 1, alignItems: 'center'}}>
                      <TextField
                        fullWidth
                        inputProps={{maxLength: CATEGORY_LABEL_MAX_CHARS}}
                        placeholder={translate('domainGuide.Detail.Rubric.CategoryLabelPlaceholder')}
                        value={category.label}
                        onChange={(label) => setCategoryField(index, 'label', label)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: 500,
                          },
                          '& .MuiOutlinedInput-input': {
                            py: '10px',
                          },
                        }}
                      />
                      <Tooltip title={translate('domainGuide.Detail.Rubric.AutoFailHint')}>
                        <Box sx={{flexShrink: 0}}>
                          <Checkbox
                            checked={category.autoFail}
                            onChange={(autoFail) => setCategoryField(index, 'autoFail', autoFail)}
                            label={
                              <Typography
                                sx={{
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  color: category.autoFail ? '#C4512C' : '#6B6761',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <Translate expression='domainGuide.Detail.Rubric.AutoFailLabel' />
                              </Typography>
                            }
                          />
                        </Box>
                      </Tooltip>
                      <IconButton
                        aria-label={translate('domainGuide.Detail.Rubric.RemoveCategory')}
                        onClick={() => removeCategory(index)}
                        size='small'
                      >
                        <DeleteOutline sx={{fontSize: 18, color: '#A8A49E'}} />
                      </IconButton>
                    </Stack>
                    <TextField
                      fullWidth
                      multiline
                      inputProps={{maxLength: CATEGORY_DESCRIPTION_MAX_CHARS}}
                      placeholder={translate('domainGuide.Detail.Rubric.CategoryDescriptionPlaceholder')}
                      value={category.description}
                      onChange={(description) => setCategoryField(index, 'description', description)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: '#6B6761',
                        },
                      }}
                    />
                  </Stack>
                </Box>
              ))
            }
          </Stack>
          <Button
            disabled={!canAddCategory}
            onClick={addCategory}
            startIcon={<AddOutlined sx={{fontSize: 16}} />}
            sx={{
              'alignSelf': 'flex-start',
              'px': '12px',
              'py': '6px',
              'border': '1px solid #E8E4DE',
              'borderRadius': '999px',
              'backgroundColor': '#FFFFFF',
              'boxShadow': 'none',
              'color': '#1A1916',
              'fontSize': '12px',
              'fontWeight': 700,
              'textTransform': 'none',
              '&:hover': {
                borderColor: '#D8D2CA',
                backgroundColor: '#F8F6F3',
                boxShadow: 'none',
              },
            }}
            variant='outlined'
          >
            <Translate expression='domainGuide.Detail.Rubric.AddCategory' />
          </Button>
        </Stack>
        <Stack direction='row' sx={{gap: 2, alignItems: 'center'}}>
          <Button
            disabled={!canSave || isSaving}
            onClick={saveRubric}
            sx={{
              'px': '16px',
              'py': '8px',
              'borderRadius': '8px',
              'backgroundColor': '#DA7955',
              'boxShadow': 'none',
              'color': '#FFFFFF',
              'fontSize': '13px',
              'fontWeight': 700,
              'textTransform': 'none',
              '&:hover': {
                backgroundColor: '#C4512C',
                boxShadow: 'none',
              },
            }}
            variant='contained'
          >
            {
              isSaving ?
                translate('domainGuide.Detail.Rubric.Saving') :
                translate('domainGuide.Detail.Rubric.Save')
            }
          </Button>
          {
            saveFailed &&
            <Typography sx={{fontSize: '12px', color: '#D85D31'}}>
              <Translate expression='domainGuide.Detail.Rubric.SaveFailed' />
            </Typography>
          }
          {
            !saveFailed &&
            <Typography sx={{fontSize: '12px', color: '#A8A49E'}}>
              {
                domainGuideData.rubric && rubricUpdatedAtLabel ?
                  translate('domainGuide.Detail.Rubric.UpdatedAt', {updatedAt: rubricUpdatedAtLabel}) :
                  translate('domainGuide.Detail.Rubric.NeverSaved')
              }
            </Typography>
          }
        </Stack>
      </Stack>
    </Box>
  );
};

DomainGuideRubric.propTypes = {
  domainGuideData: PropTypes.object.isRequired,
  updateRubric: PropTypes.func.isRequired,
};

export default React.memo(DomainGuideRubric);
